import React, { useEffect } from "react";
import Select, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import service from "../../../../services/service";
import "./reitSelector.less";
import { useReitContext } from "../../../../context/reitContext";
import {
  LocationData,
  ReitProperty,
  SelectionRow,
} from "../../../../types/reitProperty.type";

interface ReitSelectorProps {
  onData: (selectedProperties: string[]) => void;
}

const ReitSelector: React.FC<ReitSelectorProps> = ({ onData }) => {
  const displayName = "cmp-reit-selector";

  const {
    cities,
    setCities,
    rows,
    setRows,
    selectedProperties,
    setSelectedProperties,
    rawData,
    setRawData,
  } = useReitContext();

  const embassyImage = new URL(
    "../../../../assets/Embassy_1.png",
    import.meta.url
  ).href;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response: LocationData[] = await service.fetchCities();
        setRawData(response);

        const cityOptions = response
          .map((location) => ({
            value: location.location,
            label: location.location,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCities(cityOptions);

        if (cityOptions.length > 0 && rows.length === 0) {
          const firstCity = cityOptions[0];
          const firstLocation = response.find(
            (loc) => loc.location === firstCity.value
          );

          if (firstLocation && firstLocation.cities.length > 0) {
            const propertyOptions = firstLocation.cities
              .map((p) => ({ value: p.name, label: p.name, isDisabled: false }))
              .sort((a, b) => a.label.localeCompare(b.label));

            const firstProperty = propertyOptions[0];

            const initialRow: SelectionRow = {
              id: "1",
              city: firstCity,
              property: firstProperty,
              propertyOptions,
            };

            setRows([initialRow]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };
    fetchData();
  }, []);

  const handleCityChange = (
    selected: SingleValue<{ value: string; label: string }>,
    rowId: string
  ) => {
    if (!selected) return;

    const currentRow = rows.find((row) => row.id === rowId);
    if (currentRow?.property) {
      setSelectedProperties((prev: ReitProperty[]) =>
        prev.filter((p) => p.name !== currentRow.property?.value)
      );
    }

    console.log("selected properties in reits selector : ", selectedProperties);

    const updatedRows = rows.map((row) => {
      if (row.id === rowId) {
        const selectedLocation = rawData.find(
          (loc) => loc.location === selected.value
        );

        // Get already selected properties from other rows
        const selectedPropertyNames = rows
          .filter((r) => r.id !== rowId && r.property)
          .map((r) => r.property?.value);

        const propertyOptions =
          selectedLocation?.cities
            .map((p) => ({
              value: p.name,
              label: p.name,
              isDisabled: selectedPropertyNames.includes(p.name),
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

        return { ...row, city: selected, propertyOptions, property: null };
      }
      return row;
    });
    setRows(updatedRows);
    onData(
      updatedRows
        .map((row) => row.property?.value)
        .filter(
          (value): value is string => value !== undefined && value !== null
        )
    );
  };

  const handlePropertyChange = async (
    selected: SingleValue<{ value: string; label: string }>,
    rowId: string
  ) => {
    if (!selected) return;

    const currentRow = rows.find((row) => row.id === rowId);

    if (currentRow?.property) {
      setSelectedProperties((prev) =>
        prev.filter((p) => p.name !== currentRow.property?.value)
      );
    }

    const newProperty: ReitProperty = {
      name: selected.value,
      latlng: { lat: 0, lng: 0, label: selected.label },
      reitName: "Embassy",
      reitImage: embassyImage,
      assetClass: "Office",
      listingDate: "",
    };

    setSelectedProperties((prev) => [...prev, newProperty]);

    const updatedRows = rows.map((row) =>
      row.id === rowId ? { ...row, property: selected } : row
    );
    setRows(updatedRows);
    onData(
      updatedRows
        .map((row) => row.property?.value)
        .filter(
          (value): value is string => value !== undefined && value !== null
        )
    );
  };

  const handleAddRow = () => {
    if (rows.length < 3) {
      const newRow: SelectionRow = {
        id: Date.now().toString(),
        city: null,
        property: null,
        propertyOptions: [],
      };
      setRows([...rows, newRow]);
    } else {
      toast.warn("You can only select a maximum of 3 properties.");
    }
  };

  const handleRemoveRow = (rowId: string) => {
    // Count how many rows have properties selected
    const rowsWithProperties = rows.filter((row) => row.property);

    // If this is the only row with a property selected, show toast and return
    if (
      rowsWithProperties.length === 1 &&
      rows.find((row) => row.id === rowId)?.property
    ) {
      toast.warn("At least one property should be selected");
      return;
    }

    const rowToRemove = rows.find((row) => row.id === rowId);
    const newRows = rows.filter((row) => row.id !== rowId);

    if (rowToRemove?.property) {
      setSelectedProperties((prev: ReitProperty[]) =>
        prev.filter((p) => p.name !== rowToRemove.property?.value)
      );
    }

    // Update property options for all remaining rows to re-enable the removed property
    const updatedRows = newRows.map((row) => {
      if (row.propertyOptions.length > 0) {
        const selectedPropertyNames = newRows
          .filter((r) => r.id !== row.id && r.property)
          .map((r) => r.property?.value);

        const updatedPropertyOptions = row.propertyOptions.map((prop) => ({
          ...prop,
          isDisabled: selectedPropertyNames.includes(prop.value),
        }));

        return { ...row, propertyOptions: updatedPropertyOptions };
      }
      return row;
    });

    setRows(updatedRows);
    onData(
      updatedRows
        .map((row) => row.property?.value)
        .filter(
          (value): value is string => value !== undefined && value !== null
        )
    );
  };

  const handleImageClick = (rowId: string) => {
    const rowToEdit = rows.find((row) => row.id === rowId);

    // Count how many rows have properties selected
    const rowsWithProperties = rows.filter((row) => row.property);

    // If this is the only row with a property selected, show toast and return
    if (rowsWithProperties.length === 1 && rowToEdit?.property) {
      toast.warn("At least one property should be selected");
      return;
    }

    if (rowToEdit?.property) {
      setSelectedProperties((prev: ReitProperty[]) =>
        prev.filter((p) => p.name !== rowToEdit.property?.value)
      );
    }

    const updatedRows = rows.map((row) => {
      if (row.id === rowId) {
        return { ...row, city: null, property: null, propertyOptions: [] };
      }

      // Update property options for other rows to re-enable the cleared property
      if (row.propertyOptions.length > 0) {
        const selectedPropertyNames = rows
          .filter((r) => r.id !== row.id && r.id !== rowId && r.property)
          .map((r) => r.property?.value);

        const updatedPropertyOptions = row.propertyOptions.map((prop) => ({
          ...prop,
          isDisabled: selectedPropertyNames.includes(prop.value),
        }));

        return { ...row, propertyOptions: updatedPropertyOptions };
      }

      return row;
    });

    setRows(updatedRows);
    onData(
      updatedRows
        .map((row) => row.property?.value)
        .filter(
          (value): value is string => value !== undefined && value !== null
        )
    );

    console.log("rows in reits selector : ", rows);
  };

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "48px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      fontSize: "16px",
      fontFamily: '"Duplet", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.02em",
      lineHeight: "100%",
      "&:hover": {
        borderColor: "#3cbdeb",
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#666",
      fontSize: "16px",
      fontFamily: '"Duplet", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.02em",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#333",
      fontSize: "16px",
      fontFamily: '"Duplet", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.02em",
    }),
  };

  return (
    <section className={displayName}>
      <div className={`${displayName}__container`}>
        <div className={`${displayName}__location-row`}>
          {rows.map((row, index) => {
            // Get already selected properties (not cities)
            const selectedPropertyNames = rows
              .filter((r) => r.id !== row.id && r.property)
              .map((r) => r.property?.value);

            // Disable selected properties only
            const propertyOptionsWithDisabled = row.propertyOptions.map(
              (prop) => ({
                ...prop,
                isDisabled: selectedPropertyNames.includes(prop.value),
              })
            );

            return (
              <React.Fragment key={row.id}>
                {row.city && row.property ? (
                  <div className={`${displayName}__property-image-wrapper`}>
                    <div
                      className={`${displayName}__property-image`}
                      onClick={() => handleImageClick(row.id)}
                    >
                      <img src={embassyImage} alt="Property" />
                    </div>
                    <button
                      className={`${displayName}__image-remove-btn`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveRow(row.id);
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className={`${displayName}__location-selector`}>
                    <Select
                      options={cities}
                      value={row.city}
                      onChange={(e) => handleCityChange(e, row.id)}
                      placeholder="Select Location"
                      styles={customSelectStyles}
                    />
                    <Select
                      options={propertyOptionsWithDisabled}
                      value={row.property}
                      onChange={(e) => handlePropertyChange(e, row.id)}
                      placeholder="Select Property"
                      isDisabled={!row.city}
                      styles={customSelectStyles}
                      isOptionDisabled={(option) => !!option.isDisabled}
                    />
                    {index > 0 && (
                      <button
                        className={`${displayName}__remove-btn`}
                        onClick={() => handleRemoveRow(row.id)}
                        type="button"
                      >
                        ×
                      </button>
                    )}
                  </div>
                )}
                {index === rows.length - 1 && rows.length < 3 && (
                  <div className={`${displayName}__add-container`}>
                    <button
                      className={`${displayName}__add-btn`}
                      onClick={handleAddRow}
                      type="button"
                    >
                      +
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ReitSelector;
